import {useQuery} from '@apollo/client';
import {useEffect, useState, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {GET_REPORT_OPERATORS, GetReportOperatorsResponse} from '../graphql/queries/getReportOperators';
import {getUserInfo, isReportDownloadSelfOnlyRole} from '../../../../utils/auth';
import {ReportFileFiltersType} from './useWmsReportFileQuery';

interface UseReportOperatorsResult {
    operatorOptions: {value: string, label: string}[];
    loading: boolean;
    defaultFilters: ReportFileFiltersType | null;
}

export const useReportOperators = (warehouseId: number): UseReportOperatorsResult => {
    const {t, i18n} = useTranslation();
    const [operatorOptions, setOperatorOptions] = useState<{value: string, label: string}[]>([]);
    const [defaultFilters, setDefaultFilters] = useState<ReportFileFiltersType | null>(null);

    // 获取操作人列表
    const { data: operatorsData, loading } = useQuery<GetReportOperatorsResponse>(GET_REPORT_OPERATORS, {
        variables: { warehouseId },
        skip: !warehouseId,
    });

    // 获取"全部"的翻译文本，确保翻译已加载
    const allLabel = useMemo(() => {
        const translated = t('wms.download.filters.all');
        // 如果返回的是 key 本身，说明翻译未找到，使用默认值
        return translated === 'wms.download.filters.all' ? (i18n.language === 'zh' ? '全部' : 'All') : translated;
    }, [t, i18n.language]);

    // 处理操作人数据变化并设置默认过滤条件
    useEffect(() => {
        if (operatorsData?.getReportOperatorsForWms) {
            const selfOnly = isReportDownloadSelfOnlyRole();
            const mapped = operatorsData.getReportOperatorsForWms.map(operator => ({
                value: operator.id,
                label: `${operator.name}`
            }));
            const options = selfOnly ? mapped : [{ value: '', label: allLabel }, ...mapped];
            setOperatorOptions(options);

            // 设置默认过滤条件
            const currentUser = getUserInfo();
            let filters: ReportFileFiltersType = {
                type: undefined,
                name: undefined,
                // 默认日期不在这里设置：避免被写入 URL/hash
                // 由页面/筛选组件在运行时展示“最近一周”
                reportDate: undefined,
                operatorId: undefined,
            };

            if (currentUser) {
                const currentUserId = parseInt(currentUser.account.id, 10);
                if (selfOnly) {
                    filters.operatorId = currentUserId;
                } else {
                    const currentOperator = operatorsData.getReportOperatorsForWms.find(
                        operator => parseInt(operator.id, 10) === currentUserId
                    );
                    if (currentOperator) {
                        filters.operatorId = currentUserId;
                    }
                }
            }

            setDefaultFilters(filters);
        }
    }, [operatorsData, allLabel]);

    return {
        operatorOptions,
        loading,
        defaultFilters
    };
};
